import { focus, disabledTokens } from '@federa/utils/styles/interaction-tokens';
import { FULL_WIDTH_STYLE } from '@federa/const/style-size-presets';
import { FLEX_ROW_CENTER_STYLE } from '@federa/const/style-flex-presets';
import { NATIVE_PICKER_INPUT_TYPES } from '@federa/const/html-input-types';
import { colors } from '../css-refs.colors.ts';
import { typography } from '../css-refs.typography.ts';
import { spacing } from '../css-refs.spacing.ts';
import { borderRadius, motion } from '../css-refs.effects.ts';
import { divider } from '../css-refs.layout.ts';
import { buildFormFieldWrapperStyles } from './hooks/useFormFieldStyles.ts';
import { getLabelStyles as getCanonicalLabelStyles, getHelperTextStyles, componentSizeHeights } from '../inputs/form-utils.ts';
import { setFlagThen } from './compose-handler.ts';
import type * as React from 'react';
import type { ComponentSize, ComponentSizeFacets } from '@federa/types/ui-design-tokens';
import type { FormFieldState } from '@federa/types/ui-form-field';
import type { InputStateStyle } from '@federa/types/ui-input';

/**
 * WHAT `bare` DROPS, for every SDL text control that offers it -- the four members that ARE a form control's box.
 *
 * ONE DECLARATION, THREE ADOPTERS: `Textarea`, `Input`'s `default` variant (both of which draw their box on the
 * control) and `Input`'s `floating` variant (which draws its pill on the container, so the same members land there
 * instead). It lives here rather than beside any one of them because it is the DEFINITION of the variant: two copies
 * would let a control quietly mean something different by the same prop name, which is the drift the shared
 * `FieldValidityProps` contract was extracted to end on the validity half.
 *
 * SPREAD BEFORE the call site's `customStyle`, always: `bare` is the variant's own decision and a caller must still be
 * able to put one member back -- an embedded field that wants its padding, say -- exactly as it can over the boxed
 * form. The focus ring is deliberately NOT in here: it is `boxShadow`, so it survives the reset, and a field with no
 * visible focus affordance is not what "embedded in the caller's surface" asks for.
 *
 * THE PADDING IS CANCELLED AS LONGHANDS TOO (2026-09-18). `buildInputStyles` spells the default input's side padding as
 * `paddingLeft` / `paddingRight` longhands after its `padding` shorthand, and a merged style object keeps each key at
 * its FIRST position: `padding: 0` overrode the shorthand in place, the longhands that followed it stood, and a "bare"
 * input still carried sixteen pixels of side padding. Measured on the Noor rate figure, whose digits are sized by a
 * twin span and lost exactly that width to the padding. A shorthand cannot beat a longhand that follows it, so the
 * longhands are cancelled by name.
 */
export const BARE_BOX_OVERRIDES: React.CSSProperties = {
	border: 'none',
	borderRadius: 0,
	padding: 0,
	paddingLeft: 0,
	paddingRight: 0,
	backgroundColor: 'transparent',
};

/*
 * VERTICAL PADDING IS ZERO ON PURPOSE, exactly as in `SELECT_SIZE_FACETS`, and restoring it re-opens the defect at
 * every text input in the tree. Each size pins an explicit `height`, so vertical padding is not free space -- it is
 * either subtracted from a fixed box (clipping the text) or added outside it (rendering the control taller than the
 * control beside it), depending on the ambient box model. Both readings were witnessed on this wave. Every size here
 * carried the SAME contradiction the select facets did: 32/40/48px boxes less 16/24/32px of padding against `normal`
 * line boxes of 16.8/19.2/21.6px.
 *
 * FOUND BY `DS-SIZE-FACET-GEOMETRY-CONTRADICTION-001`, not by review -- this record is the rule's day-one queue, and it
 * had survived the select cure because nothing connected the two records. Anything wanting a taller input raises
 * `componentSizeHeights` (`components/inputs/form-utils.ts`), never this padding.
 */
export const inputSizeStyles: Record<ComponentSize, ComponentSizeFacets> = {
	sm: {
		padding: `0 ${spacing[3]}`,
		fontSize: typography.fontSize.sm,
		height: componentSizeHeights.sm,
	},
	md: {
		padding: `0 ${spacing[4]}`,
		fontSize: typography.fontSize.base,
		height: componentSizeHeights.md,
	},
	lg: {
		padding: `0 ${spacing[5]}`,
		fontSize: typography.fontSize.lg,
		height: componentSizeHeights.lg,
	},
};

export const inputStateStyles: Record<FormFieldState, InputStateStyle> = {
	default: {
		borderColor: colors.border.default,
		focusBorderColor: colors.brand.primary,
	},
	error: {
		borderColor: colors.semantic.error,
		focusBorderColor: colors.semantic.error,
	},
	success: {
		borderColor: colors.semantic.success,
		focusBorderColor: colors.semantic.success,
	},
};

export function getInputWrapperStyles(fullWidth: boolean): React.CSSProperties {
	return buildFormFieldWrapperStyles(fullWidth, spacing[1]);
}

export function getInputContainerStyles(): React.CSSProperties {
	return {
		position: 'relative',
		...FLEX_ROW_CENTER_STYLE,
		...FULL_WIDTH_STYLE,
	};
}

export function getBaseInputStyles(disabled: boolean | undefined): React.CSSProperties {
	return {
		...FULL_WIDTH_STYLE,
		fontFamily: typography.fontFamily.sans,
		fontWeight: typography.fontWeight.normal,
		color: disabled ? disabledTokens.color : colors.text.primary,
		backgroundColor: disabled ? disabledTokens.background : colors.background.primary,
		border: `${divider.thickness.thin} solid`,
		borderRadius: borderRadius.md,
		outline: 'none',
		transition: `border-color ${motion.duration.fast}, box-shadow ${motion.duration.fast}`,
	};
}

/** The PRIMITIVE label preset: the canonical `getLabelStyles` (`components/inputs/form-utils.ts`) bound to the two
 *  values every primitive label uses. Named for what it is rather than sharing the canonical name -- both are reachable
 *  through `@federa/components/<subpath>`, so one name for a parameterized function and its zero-arg preset made which
 *  one a caller got depend on the import path (`QUAL-CROSS-DIR-EXPORT-NAME-COLLISION-001`). */
export function getPrimitiveLabelStyles(): React.CSSProperties {
	return getCanonicalLabelStyles({
		colorOverride: colors.text.primary,
		marginBottom: spacing[1],
	});
}

export function getMessageStyles(state: FormFieldState): React.CSSProperties {
	return getHelperTextStyles({
		state,
		size: 'sm',
		marginTop: spacing[1],
		colorDefault: colors.text.secondary,
	});
}

/**
 * Element-generic because the focus-tracking idiom is the same for `<input>`, `<select>` and `<textarea>`, and the one
 * thing a per-element copy of it would guarantee is that only one of the three keeps composing the caller's handler
 * correctly (see `composeHandler`'s docstring for what that costs). `E` defaults to `HTMLInputElement`, so the existing
 * `InputCore` call site is unchanged.
 *
 * `E` IS UNCONSTRAINED ON PURPOSE. An `E extends HTMLElement` bound reads tighter but buys nothing here -- `E` is only
 * ever fed to `React.FocusEventHandler<E>`, which is itself unconstrained -- and it made the function's typability
 * depend on which `lib.dom` a CONSUMING project loads: instantiating it at `HTMLSelectElement` typechecked under the
 * components program and failed under the Next app's with a `remove()` return-type variance error. A bound that can be
 * satisfied in one project and not another is a portability trap, not a safety net.
 */
export function buildInputFocusHandlers<E = HTMLInputElement>(
	setIsFocused: React.Dispatch<React.SetStateAction<boolean>>,
	props: {
		onFocus?: React.FocusEventHandler<E>;
		onBlur?: React.FocusEventHandler<E>;
	},
): {
	onFocus: React.FocusEventHandler<E>;
	onBlur: React.FocusEventHandler<E>;
} {
	return {
		onFocus: setFlagThen(setIsFocused, true, props.onFocus),
		onBlur: setFlagThen(setIsFocused, false, props.onBlur),
	};
}

/**
 * The two `aria-*` attributes the input derives from its OWN state, and ONLY the ones it actually has an answer for.
 *
 * EVERY KEY USED TO BE PRESENT WITH A SOMETIMES-`undefined` VALUE, which makes the object a landmine wherever it is
 * spread: an `undefined` in a spread still WRITES the key, so spreading this after a caller's `{...props}` deleted
 * their `aria-describedby` on every input that carries no helper text of its own -- the same shape that silently
 * dropped the caller's `placeholder` for every default-variant `Input` in the repo (see `InputCore`). A conditional
 * spread is the repo's idiom for this and it is what makes the object safe at any position.
 */
export function buildInputAriaProps(
	state: FormFieldState,
	displayHelperText?: string,
	helperId?: string,
): {
	'aria-invalid'?: boolean;
	'aria-describedby'?: string;
} {
	return {
		...(state === 'error' ? { 'aria-invalid': true } : {}),
		...(displayHelperText && helperId ? { 'aria-describedby': helperId } : {}),
	};
}

/**
 * The focus-ring slice of an input's style bag, resolved from STATE instead of written onto the DOM node.
 *
 * REPLACES a `handleInputFocus` / `resetInputBorderStyles` pair that assigned `currentTarget.style.borderColor` and
 * `.boxShadow` on focus and blanked them on blur -- the second styling channel React does not know about that
 * `getCloseButtonStyles` cured in `CloseButton.tsx`, and the reason this leaf was the last carrier keeping
 * `components/primitives/**` excluded from `QUAL-INLINE-HOVER-STYLE-MUTATION-001`. A DOM write outlives the next
 * render only by accident: any re-render that rewrites `style` silently drops the ring, and no ancestor can read the
 * focused identity to compose against it.
 *
 * RETURNS THE FOCUSED SLICE ONLY, never a resting one. The resting `borderColor` is already decided by
 * `inputStateStyles[state]` upstream of the caller's own `style` prop, so emitting a resting slice here would invert
 * that precedence and let this helper beat a caller override it never used to beat; spreading `{}` at rest keeps the
 * caller's bag intact, and spreading the ring last when focused reproduces exactly what the DOM write did.
 */
export function getInputFocusRingStyles(focused: boolean): React.CSSProperties {
	return focused ? { borderColor: focus.ring.default.color, boxShadow: focus.ring.default.boxShadow } : {};
}

export function deriveInputState(error?: string, success?: string, stateProp?: FormFieldState): FormFieldState {
	return error ? 'error' : success ? 'success' : (stateProp ?? 'default');
}

export function deriveInputMessage(error?: string, success?: string, helperText?: string): string | undefined {
	return error || success || helperText;
}

/**
 * A PRESS ANYWHERE ON A PICKER BOX OPENS ITS PICKER (operator, 2026-09-28: "when i click the date the calendar doesnt
 * open"). A native date, time, month or week box opens its calendar or clock only from its own small icon, and a press
 * on the rest of it only moves a caret into its segments. `showPicker` is the platform's one door to that picker; it
 * throws where the page may not open it (a cross-origin frame, a press the browser does not count), and the box then
 * stays the typed one it always was. A box that takes no input stays shut. The caller's own press runs first.
 */
export function openPickerOnPress(onClick?: React.MouseEventHandler<HTMLInputElement>): React.MouseEventHandler<HTMLInputElement> {
	return (event) => {
		onClick?.(event);
		const box = event.currentTarget;
		if (!NATIVE_PICKER_INPUT_TYPES.has(box.type) || box.disabled || box.readOnly) return;
		try {
			box.showPicker();
		} catch {
			// Silent by design: not openable from here (a cross-origin frame, a press the browser does not count), so the
			// box keeps its own icon and its typed segments, exactly what it had before the press.
		}
	};
}

/** The wrapper of a box the browser's picker fills: as wide as its value or its label, never the row's width -- a date
 *  stretched across a form reads as a box to write a sentence in (operator, 2026-09-28: "the field itself is too
 *  big"). A caller's `fullWidth` still wins. */
export function pickerWrapperStyles(type: unknown, fullWidth: boolean): React.CSSProperties {
	return !fullWidth && NATIVE_PICKER_INPUT_TYPES.has(type) ? { width: 'fit-content' } : {};
}

export function getLabelId(id: string): string {
	return `${id}-label`;
}

export function getMessageId(id: string): string {
	return `${id}-message`;
}

export const adornmentBaseStyles: React.CSSProperties = {
	...FLEX_ROW_CENTER_STYLE,
	position: 'absolute',
	top: '50%',
	transform: 'translateY(-50%)',
	pointerEvents: 'none',
};

export const adornmentLeftStyles: React.CSSProperties = {
	...adornmentBaseStyles,
	left: spacing[3],
};
